import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowRight, CheckCircle2, Mail } from 'lucide-react'
import { AuthLayout } from '../../components/layout/AuthLayout'
import { api, extractApiError } from '../../lib/api'
import { emailSchema } from '../../lib/validators'

const schema = z.object({
  emailId: emailSchema(),
})

export function ForgotPasswordPage() {
  // Once the request succeeds, swap to a confirmation panel rather
  // than letting the user retype their email — matches the back-end
  // behaviour, which is silent-OK regardless of whether the email
  // matches a real account, so re-submitting tells the user nothing.
  const [submitted, setSubmitted] = useState(false)

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { emailId: '' },
  })

  async function onSubmit(values) {
    try {
      await api.post('/auth/forgot-password', { emailId: values.emailId })
      setSubmitted(true)
    } catch (err) {
      const { message, fields } = extractApiError(err)
      if (fields?.emailId) {
        form.setError('emailId', { message: fields.emailId })
      } else {
        toast.error(message)
      }
    }
  }

  // Confirmation panel — same centered hero treatment as the form,
  // with a success icon swap-in for the floating logo.
  if (submitted) {
    return (
      <AuthLayout
        centered
        title="Check Your Email"
        subtitle="If an account exists for that Email ID, a password-reset link has been sent. The link expires in 60 minutes and can only be used once."
      >
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4 text-sm text-[color:var(--color-ink-muted)] flex items-start gap-3">
          <CheckCircle2
            size={18}
            className="text-[color:var(--color-success)] shrink-0 mt-0.5"
          />
          <p className="m-0">
            Didn't receive it? Check your spam folder, then{' '}
            <button
              type="button"
              onClick={() => {
                setSubmitted(false)
                form.reset()
              }}
              className="text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)] font-semibold underline underline-offset-2 cursor-pointer"
            >
              try again
            </button>
            .
          </p>
        </div>

        <p className="mt-6 text-center text-sm text-[color:var(--color-ink-muted)]">
          Remembered it?{' '}
          <Link
            to="/sign-in"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Back to Sign In
          </Link>
        </p>
      </AuthLayout>
    )
  }

  const emailError = form.formState.errors.emailId?.message
  const submitting = form.formState.isSubmitting

  return (
    <AuthLayout
      centered
      title="Forgot Password?"
      subtitle="Enter your Email ID and we'll send a reset link."
    >
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        className="space-y-5"
      >
        <div>
          <label
            htmlFor="emailId"
            className="block w-fit mb-1.5 text-[14px] font-medium text-[color:var(--color-ink-strong)]"
          >
            Email ID
            <span
              aria-hidden="true"
              className="ml-0.5 text-[color:var(--color-priority-urgent)]"
            >
              *
            </span>
          </label>
          <div className="relative">
            <Mail
              size={16}
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)]"
            />
            <input
              id="emailId"
              type="email"
              autoComplete="email"
              placeholder="Enter the Email ID"
              className={
                'w-full h-11 pl-10 pr-3 rounded-xl border bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-primary)] ' +
                (emailError
                  ? 'border-[color:var(--color-priority-urgent)]'
                  : 'border-[color:var(--color-rule)]')
              }
              {...form.register('emailId')}
            />
          </div>
          {emailError ? (
            <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
              {emailError}
            </p>
          ) : null}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="group w-full h-12 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 shadow-[0_8px_20px_-6px_rgba(2,54,123,0.35)] hover:shadow-[0_12px_28px_-6px_rgba(2,54,123,0.45)] transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          style={{
            background:
              'linear-gradient(135deg, #02367B 0%, #006CA5 50%, #0496C7 100%)',
          }}
        >
          {submitting ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : null}
          Send Reset Link
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </button>

        <p className="text-center text-sm text-[color:var(--color-ink-muted)]">
          Remembered it?{' '}
          <Link
            to="/sign-in"
            className="font-semibold text-[color:var(--color-primary)] hover:text-[color:var(--color-primary-hover)]"
          >
            Back to Sign In
          </Link>
        </p>
      </form>
    </AuthLayout>
  )
}
